<template>
  <div :class="ns.b()">
    <teleport v-if="active && transfer" to="body">
      <div
        ref="maskContainer"
        :style="{
          zIndex: zIndex,
          backgroundColor:
            permeable == true ? 'transparent' : 'rgba(0, 0, 0, 0.4)',
        }"
        :class="[ns.e('mask-inner')]"
        @click="handleMaskClick"
      >
        <div v-if="$slots.mask" :class="ns.e('mask')">
          <slot name="mask" />
        </div>
        <div
          ref="maskContent"
          :style="{
            backgroundColor: permeable ? 'rgba(0, 0, 0, 0.4)' : 'transparent',
          }"
          :class="[ns.e('custom-mask')]"
          @click.stop
        >
          <slot />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import { useZIndex } from './useZindex'
import { maskerProps } from './masker'

defineOptions({
  name: 'LtMasker',
})
const props = defineProps(maskerProps)
const ns = useNamespace('masker')
const emit = defineEmits<{
  (e: 'update:active', value: boolean): void
}>()

const maskContainer = ref<HTMLElement | null>(null)
const maskContent = ref<HTMLElement | null>(null)
const zIndex = ref<number>(0)

const { nextZIndex } = useZIndex()

const handleMaskClick = async () => {
  if (!props.closable) return
  if (props.onBeforeClose) {
    const shouldClose = await props.onBeforeClose()
    if (!shouldClose) return
  }
  emit('update:active', false)
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && !props.disableEsc) {
    handleMaskClick()
  }
}

const trapFocus = () => {
  if (!maskContent.value) return
  const focusableElements = maskContent.value.querySelectorAll(
    'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ) as NodeListOf<HTMLElement>
  if (focusableElements.length > 0) {
    focusableElements[0].focus()
  }
}

const preventScroll = (event: WheelEvent) => {
  if (!props.permeable) {
    event.preventDefault()
  }
}

watch(
  () => props.active,
  (newValue) => {
    if (newValue) {
      zIndex.value = nextZIndex()
      document.body.style.overflow = 'hidden'
      window.addEventListener('resize', handleResize)
      window.addEventListener('keydown', handleKeyDown)
      window.addEventListener('wheel', preventScroll, { passive: false })
      nextTick(() => {
        trapFocus()
      })
    } else {
      document.body.style.overflow = ''
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('wheel', preventScroll)
    }
  }
)

const handleResize = () => {
  if (props.onResize) {
    props.onResize()
  }
}

onMounted(() => {
  if (props.active) {
    zIndex.value = nextZIndex()
    document.body.style.overflow = 'hidden'
    window.addEventListener('resize', handleResize)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('wheel', preventScroll, { passive: false })
    nextTick(() => {
      trapFocus()
    })
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('wheel', preventScroll)
})

defineExpose({
  handleMaskClick,
})
</script>
